// Support inbox. Claude answers first and hands over what it can't resolve; "Needs staff" is the
// queue. A ticket opens as a conversation with the user's account on the side.
// Tickets in other languages read in English (translated when the ticket opens, original one
// click away), and replies written in English go out in the user's language after a preview.
// Staff can put a ticket on hold until a time (it leaves the queue and comes back by itself), and
// link it to one of the user's cash-outs, whose outcome support then posts into it.
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlarmClock,
  ArrowLeft,
  Bot,
  ChevronDown,
  CircleCheck,
  Clock,
  Gift,
  Languages,
  LifeBuoy,
  Mail,
  MessageCircleQuestion,
  Link2,
  Plus,
  RotateCcw,
  Send,
} from 'lucide-react';
import {
  SNOOZE_LABELS,
  SNOOZE_PRESETS,
  snoozeUntil,
  type SnoozePreset,
} from '@gaintplay/core/snooze';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { useAssistantContext } from '../lib/assistant';
import {
  actorLabel,
  Card,
  EmptyState,
  ErrorNote,
  fmtMoney,
  fmtNum,
  Initial,
  PageHeader,
  Pill,
  run,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
  toast,
  UserChip,
  ViewOnly,
} from '../components/ui';

interface Row {
  id: string;
  subject: string | null;
  status: string;
  aiHandled: boolean;
  /** The user's language (ISO 639-1) once their messages were translated. */
  language: string | null;
  updatedAt: number;
  /** While on hold: when it comes back to "Needs staff". */
  snoozedUntil: number | null;
  /** The cash-out it follows, if linked. */
  withdrawalId: string | null;
  userId: string | null;
  username: string | null;
  /** Where it came from: the site's Help, or email to support@ (docs/SUPPORT_EMAIL.md). */
  channel: 'chat' | 'email';
  /** The sender's address, on an email ticket. */
  email: string | null;
}
interface Msg {
  id: string;
  author: string;
  /** What the user wrote or reads. */
  body: string;
  /** Language of `body`; null until translated. */
  language: string | null;
  /** English version when `body` isn't English. */
  english: string | null;
  createdAt: number;
  /** Screenshots and videos the user sent (rebuilt copies; docs/SUPPORT_ATTACHMENTS.md). */
  attachments?: {
    id: string;
    kind: 'image' | 'video';
    width: number | null;
    height: number | null;
    durationMs: number | null;
    removed: boolean;
  }[];
  /** On an email ticket: what the email this message came in as carried. */
  email?: {
    from: string;
    authenticated: boolean;
    droppedFiles: string[];
    category: string | null;
  };
  /** On an email ticket, a reply we send: when SES took it, or why it did not go out. */
  emailSentAt?: number | null;
  emailError?: string | null;
}

const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
/** "ne" → "Nepali". */
export const languageName = (code: string) => {
  try {
    return languageNames.of(code) ?? code;
  } catch {
    return code;
  }
};
const foreign = (code: string | null): code is string => !!code && code !== 'en';

/** "Needs staff" is handed-over tickets plus new ones Claude hasn't answered within 10 minutes. */
const TABS = [
  { key: 'needs_staff', label: 'Needs staff' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'pending_user', label: 'Waiting for user' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
  { key: 'email', label: 'Email' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const EMPTY: Record<TabKey, [string, string]> = {
  needs_staff: [
    'Nobody is waiting on you',
    'When Claude hands a ticket over, or a held one’s time comes, it lands here.',
  ],
  on_hold: [
    'Nothing on hold',
    'Put a ticket on hold from its page while you wait on something. It comes back to Needs staff at the time you pick, or as soon as the user writes.',
  ],
  pending_user: [
    'Nobody to wait for',
    'Tickets answered and waiting for the user. They close by themselves when the user doesn’t reply in time (set in Settings).',
  ],
  closed: ['No closed tickets', 'Nothing in this tab right now. Try another one.'],
  all: ['No tickets yet', 'Tickets appear here as users write in.'],
  email: ['No email read yet', 'Mail to support@ appears here once the inbox is set up.'],
};

/** "Sat 24 Oct, 10:00": when a held ticket wakes, in the reader's time zone. */
export const wakeTime = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

export function Support() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'needs_staff') as TabKey;
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['support', tab],
    queryFn: () =>
      api<{ tickets: Row[]; counts: { status: string; n: number }[] }>(
        `/support${tab === 'all' ? '' : `?status=${tab}`}`,
      ),
    refetchInterval: 15_000,
    placeholderData: (p) => p,
    enabled: tab !== 'email',
  });
  const n = (k: string) => data?.counts.find((c) => c.status === k)?.n ?? 0;
  return (
    <div className="space-y-5">
      <PageHeader
        title="Support"
        description="Claude answers tickets first and hands over anything about money, restrictions or disputes. Once staff reply or put it on hold, Claude stays out of that ticket until it’s closed and the user writes again. Users can close a conversation once a team member has replied in it; until then a “thanks” gets a short goodbye from Claude and it closes by itself later, so nobody can close one and open another straight away."
      />
      <Tabs
        label="Ticket status"
        value={tab}
        onChange={(k) =>
          setParams((p) => (k === 'needs_staff' ? p.delete('tab') : p.set('tab', k), p), {
            replace: true,
          })
        }
        items={TABS.map((t) => ({
          key: t.key,
          label: t.label,
          count: t.key === 'all' || t.key === 'closed' || t.key === 'email' ? undefined : n(t.key),
          hot: t.key === 'needs_staff',
        }))}
      />
      {tab === 'email' && <EmailInbound />}
      {tab !== 'email' && <ErrorNote error={error} onRetry={() => void refetch()} />}
      {tab !== 'email' && !(error && !data) && (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.tickets.length === 0 ? (
            <EmptyState icon={LifeBuoy} title={EMPTY[tab][0]}>
              {EMPTY[tab][1]}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {data.tickets.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/support/${t.id}`}
                    className="flex items-center gap-3 px-4 py-3.5 transition hover:bg-[#fbf8f3] focus-visible:-outline-offset-2 sm:gap-4 sm:px-5"
                  >
                    <Initial name={t.username ?? t.email ?? '?'} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-3">
                        <span className="min-w-0 flex-1 truncate text-[15px] font-bold">
                          {t.subject ?? '(no subject)'}
                        </span>
                        <span className="shrink-0 text-[12px] tabular-nums text-stone sm:hidden">
                          <TimeAgo t={t.updatedAt} />
                        </span>
                      </div>
                      <div className="mt-0.5 flex min-w-0 items-center gap-2 text-[13px] text-stone">
                        <span className="shrink-0 sm:hidden">
                          <StatusPill status={t.status} />
                        </span>
                        <span className="truncate">
                          {t.username ?? t.email ?? 'Unknown user'}
                          {t.channel === 'email' && ' · Email'}
                          {t.status === 'on_hold' && t.snoozedUntil
                            ? ` · Wakes ${wakeTime(t.snoozedUntil)}`
                            : t.aiHandled && ' · Claude replied'}
                          {t.withdrawalId && ' · Cash-out linked'}
                          {foreign(t.language) && ` · ${languageName(t.language)}`}
                        </span>
                      </div>
                    </div>
                    <span className="hidden shrink-0 sm:block">
                      <StatusPill status={t.status} />
                    </span>
                    <span className="hidden w-20 shrink-0 text-right text-[13px] tabular-nums text-stone sm:block">
                      <TimeAgo t={t.updatedAt} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

interface Cashout {
  id: string;
  status: string;
  coins: number;
  item: { amount: number; productName: string; productCurrency: string | null } | null;
  createdAt: number;
}
interface TicketData {
  ticket: Row & {
    createdAt: number;
    snoozedBy: string | null;
    /** Staff's note from when it was put on hold; the user never sees it. */
    snoozeNote: string | null;
    /** Claude's latest note for the team (what it gathered); the user never sees it. */
    aiNote: string | null;
    /** How the linked cash-out ended, once support posted it here. */
    cashoutOutcome: string | null;
  };
  messages: Msg[];
  canTranslate: boolean;
  user: {
    id: string;
    username: string;
    email: string | null;
    status: string;
    createdAt: number;
    emailVerifiedAt: number | null;
  } | null;
  balance: { available: number; pending: number; withdrawing: number } | null;
  recentWithdrawals: Cashout[];
  /** The cash-out this ticket follows: support posts here when it ends. */
  linkedWithdrawal: (Cashout & { amountMinor: number }) | null;
  staffNames: Record<string, string>;
}

/** Messages support posted by itself; the user reads them as from GaintPlay support. */
const SYSTEM_MESSAGES: Record<string, { label: string; icon: ComponentType<{ size?: number }> }> = {
  'system:cashout': { label: 'Cash-out update, sent automatically', icon: Gift },
  'system:auto_close': { label: 'Closed after no reply, sent automatically', icon: Clock },
};
const OPEN_CASHOUT = new Set([
  'requested',
  'risk_review',
  'on_hold',
  'kyc_pending',
  'approved',
  'processing',
]);
const cashoutTitle = (w: Cashout) =>
  w.item
    ? `${fmtMoney(w.item.amount, w.item.productCurrency ?? 'USD')} ${w.item.productName}`
    : `${fmtNum(w.coins)} coins`;

/** "2026-10-24T10:00" for a datetime-local field, in the reader's time zone. */
const localInput = (t: number) =>
  new Date(t - new Date(t).getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

/**
 * A button that opens a small panel below it (as Users → Status). Closes on a pick, a click
 * outside or Escape. `edge`: on phones it opens from the button's left edge (header actions start
 * at the left there), on wider screens from its right.
 */
interface InboundRow {
  id: string;
  fromEmail: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: number;
  decision: 'ticket' | 'ignored' | 'skipped' | 'own';
  category: string | null;
  reason: string | null;
  authenticated: boolean;
  ticketId: string | null;
  droppedFiles: string[] | null;
}
/** A reply on an email ticket that did not go out (lib/support-email.ts). */
interface UnsentRow {
  id: string;
  ticketId: string;
  author: string;
  createdAt: number;
  error: string | null;
  subject: string | null;
}
const DECISION: Record<
  InboundRow['decision'],
  { label: string; tone: NonNullable<ComponentProps<typeof Pill>['tone']> }
> = {
  ticket: { label: 'Ticket', tone: 'mint' },
  ignored: { label: 'Kept out', tone: 'neutral' },
  skipped: { label: 'Skipped', tone: 'sun' },
  own: { label: 'Our copy', tone: 'sky' },
};
const CATEGORY: Record<string, string> = {
  member_support: 'Member',
  question: 'Question',
  partnership: 'Partnership',
  business: 'Business',
  vendor_spam: 'Vendor spam',
  scam: 'Scam',
  newsletter: 'Newsletter',
  unclear: 'Unclear',
  automated: 'Automated',
};

/**
 * Everything read from the support drop box (docs/SUPPORT_EMAIL.md) and what became of it. What
 * was kept out (vendors, scams, newsletters, machine mail) can be opened as a ticket after all.
 */
function EmailInbound() {
  const qc = useQueryClient();
  const can = useCan();
  const canOpen = can('support.reply');
  const [which, setWhich] = useState<'ignored' | 'all'>('ignored');
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['support-inbound', which],
    queryFn: () =>
      api<{
        emails: InboundRow[];
        counts: { decision: string; n: number }[];
        unsent: UnsentRow[];
        configured: boolean;
      }>(`/support/inbound${which === 'all' ? '' : '?decision=ignored'}`),
    refetchInterval: 30_000,
    placeholderData: (p) => p,
  });
  const n = (k: string) => data?.counts.find((c) => c.decision === k)?.n ?? 0;
  async function open(id: string) {
    const r = await run(
      () => api<{ ticketId: string }>(`/support/inbound/${id}/open`, { body: {} }),
      'Opened as a ticket, in Needs staff.',
    );
    if (r) void qc.invalidateQueries({ queryKey: ['support-inbound'] });
  }
  async function sendAgain(row: UnsentRow) {
    const r = await run(
      () => api(`/support/${row.ticketId}/messages/${row.id}/resend`, { body: {} }),
      'Sending again. It leaves this list once it has gone.',
    );
    if (r) void qc.invalidateQueries({ queryKey: ['support-inbound'] });
  }
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-stone">
        Mail to support@gaintplay.com, read every 5 minutes. Members and questions become tickets
        Claude answers; partners and companies wait here for a person; vendors, scams and
        newsletters are kept out, and can be opened as a ticket after all.
      </p>
      {data && data.unsent.length > 0 && (
        <section className="card overflow-hidden" aria-labelledby="unsent-replies">
          <h3
            id="unsent-replies"
            className="flex items-center gap-2 border-b border-hairline px-4 py-3 text-[14px] font-bold sm:px-5"
          >
            <Mail size={15} aria-hidden="true" /> Replies that did not go out
            <span className="font-normal text-stone">· {data.unsent.length}</span>
          </h3>
          <ul className="divide-y divide-hairline">
            {data.unsent.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-[13px] sm:px-5"
              >
                <Link
                  to={`/support/${row.ticketId}`}
                  className="min-w-0 flex-1 truncate font-semibold underline-offset-2 hover:underline"
                >
                  {row.subject ?? '(no subject)'}
                </Link>
                <span className="text-stone">
                  {row.author.startsWith('ai:') ? 'Claude' : 'Staff'} ·{' '}
                  <TimeAgo t={row.createdAt} />
                </span>
                <span className="font-semibold text-berry-deep">{row.error}</span>
                {canOpen && (
                  <button
                    type="button"
                    className="btn-ghost min-h-9 px-3 py-1.5 text-[13px]"
                    onClick={() => void sendAgain(row)}
                  >
                    Send again
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <Tabs
        label="What to show"
        value={which}
        onChange={setWhich}
        items={[
          { key: 'ignored', label: 'Kept out', count: n('ignored') },
          {
            key: 'all',
            label: 'Everything',
            count: n('ticket') + n('ignored') + n('skipped') + n('own'),
          },
        ]}
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {!(error && !data) && (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : data && !data.configured ? (
            <EmptyState icon={Mail} title="Support by email isn’t set up">
              Add the drop box and its app password to the deploy’s secrets (docs/SUPPORT_EMAIL.md).
            </EmptyState>
          ) : !data || data.emails.length === 0 ? (
            <EmptyState
              icon={Mail}
              title={which === 'ignored' ? 'Nothing kept out' : 'No email read yet'}
            >
              {which === 'ignored'
                ? 'Every email read so far became a ticket.'
                : 'Mail to support@ appears here once the first poll has run.'}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {data.emails.map((e) => {
                const d = DECISION[e.decision];
                return (
                  <li key={e.id} className="flex items-start gap-3 px-4 py-3.5 sm:gap-4 sm:px-5">
                    <Initial name={e.fromName ?? e.fromEmail} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-3">
                        <span className="min-w-0 flex-1 truncate text-[15px] font-bold">
                          {e.subject ?? '(no subject)'}
                        </span>
                        <span className="shrink-0 text-[12px] tabular-nums text-stone">
                          <TimeAgo t={e.receivedAt} />
                        </span>
                      </div>
                      <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-stone">
                        <span className="truncate">
                          {e.fromName ? `${e.fromName} · ` : ''}
                          {e.fromEmail}
                        </span>
                        <Pill tone={d.tone}>{d.label}</Pill>
                        {e.category && (
                          <Pill tone="neutral">{CATEGORY[e.category] ?? e.category}</Pill>
                        )}
                        {!e.authenticated && e.decision !== 'own' && (
                          <Pill tone="sun">Not verified</Pill>
                        )}
                      </div>
                      {e.reason && (
                        <p className="mt-1 text-[13px] text-graphite">
                          {e.reason}
                          {e.droppedFiles &&
                            e.droppedFiles.length > 0 &&
                            ` · files not kept: ${e.droppedFiles.join(', ')}`}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0">
                      {e.ticketId ? (
                        <Link
                          to={`/support/${e.ticketId}`}
                          className="btn-ghost min-h-9 px-3 py-1.5 text-[13px]"
                        >
                          Open ticket
                        </Link>
                      ) : canOpen && (e.decision === 'ignored' || e.decision === 'skipped') ? (
                        <button
                          type="button"
                          className="btn-ghost min-h-9 px-3 py-1.5 text-[13px]"
                          onClick={() => void open(e.id)}
                        >
                          Open as ticket
                        </button>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Menu({
  label,
  icon: Icon,
  wide = false,
  edge = 'right',
  compact = false,
  children,
}: {
  label: string;
  icon: ComponentType<{ size?: number }>;
  wide?: boolean;
  edge?: 'right' | 'responsive';
  /** A smaller button, for a card header. */
  compact?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onDown = (e: PointerEvent) => {
      if (d.open && !d.contains(e.target as Node)) d.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && d.open) {
        d.open = false;
        d.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  const close = () => {
    const d = ref.current;
    if (!d) return;
    d.open = false;
    // The busy state and focus return land on the visible button.
    d.querySelector('summary')?.focus();
  };
  return (
    <details ref={ref} className="group relative">
      <summary
        className={`btn-ghost cursor-pointer list-none group-open:border-ink [&::-webkit-details-marker]:hidden ${
          compact
            ? 'min-h-8 px-2.5 py-1 text-[13px] max-sm:min-h-10'
            : 'min-h-10 px-3.5 py-2 text-[14px]'
        }`}
      >
        <span aria-hidden="true" className="contents">
          <Icon size={15} />
        </span>{' '}
        {label}
        <ChevronDown size={14} aria-hidden="true" className="transition group-open:rotate-180" />
      </summary>
      <div
        className={`absolute z-20 mt-2 rounded-[14px] border-[1.5px] border-ink bg-paper p-1.5 shadow-[3px_3px_0_#111110] ${
          wide ? 'w-[min(22rem,calc(100vw-2rem))]' : 'w-[min(19rem,calc(100vw-2rem))]'
        } ${edge === 'responsive' ? 'left-0 sm:left-auto sm:right-0' : 'right-0'}`}
      >
        {children(close)}
      </div>
    </details>
  );
}

type HoldChoice = { preset: SnoozePreset } | { until: number };

/** Put on hold: an optional note for the team, then a quick choice or a date and time. */
function HoldMenu({ onHold }: { onHold: (choice: HoldChoice, note: string) => void }) {
  const ids = useId();
  const [note, setNote] = useState('');
  const [custom, setCustom] = useState('');
  const now = Date.now();
  const at = custom ? new Date(custom).getTime() : NaN;
  const customOk = Number.isFinite(at) && at >= now + 5 * 60_000 && at <= now + 30 * 86_400_000;
  return (
    <Menu label="Put on hold" icon={AlarmClock} wide edge="responsive">
      {(close) => {
        const pick = (choice: HoldChoice) => {
          close();
          onHold(choice, note);
          setNote('');
          setCustom('');
        };
        return (
          <>
            <div className="px-1.5 pb-2 pt-1">
              <label className="label" htmlFor={`${ids}-n`}>
                Note for the team <span className="font-normal text-stone">(optional)</span>
              </label>
              <textarea
                id={`${ids}-n`}
                className="input min-h-[60px] resize-y text-[14px] leading-snug"
                maxLength={500}
                placeholder="e.g. Waiting for WizzGift to confirm the delivery"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="px-3 pb-1 pt-1 text-[12px] font-semibold text-stone">
              Back in Needs staff
            </div>
            {SNOOZE_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => pick({ preset: p })}
                className="flex w-full flex-wrap items-baseline justify-between gap-x-3 rounded-[10px] px-3 py-2 text-left transition hover:bg-canvas"
              >
                <span className="text-[14px] font-bold">{SNOOZE_LABELS[p]}</span>
                <span className="text-[12px] tabular-nums text-stone">
                  {wakeTime(snoozeUntil(p, now))}
                </span>
              </button>
            ))}
            <form
              className="mx-1.5 mt-1.5 border-t border-hairline pb-1 pt-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (customOk) pick({ until: at });
              }}
            >
              <label className="label" htmlFor={`${ids}-d`}>
                Or until <span className="font-normal text-stone">(your time)</span>
              </label>
              <div className="flex gap-2">
                <input
                  id={`${ids}-d`}
                  type="datetime-local"
                  className="input h-10 min-w-0 flex-1 py-0 text-[14px] tabular-nums"
                  min={localInput(now + 5 * 60_000)}
                  max={localInput(now + 30 * 86_400_000)}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                />
                <button
                  className="btn-primary h-10 shrink-0 px-3.5 py-0 text-[14px]"
                  disabled={!customOk}
                >
                  Hold
                </button>
              </div>
            </form>
            <p className="px-3 pb-1.5 pt-2 text-[12px] leading-snug text-stone">
              The user sees “In progress” and gets no email. It comes back sooner if they write.
            </p>
          </>
        );
      }}
    </Menu>
  );
}

/**
 * The cash-out this ticket follows. When it's paid, rejected or fails, support posts that here
 * (paid closes the ticket only if nobody needs staff); the cash-out's own email tells the user.
 * The text says what will actually happen: nothing for a closed ticket or a cash-out that ended.
 */
function CashoutCard({
  linked,
  recent,
  manage,
  closed,
  posted,
  onLink,
  onUnlink,
}: {
  linked: TicketData['linkedWithdrawal'];
  recent: Cashout[];
  manage: boolean;
  /** The ticket is closed. */
  closed: boolean;
  /** How it ended, once support posted it here. */
  posted: string | null;
  onLink: (w: Cashout) => void;
  onUnlink: () => void;
}) {
  const choices = recent.filter((w) => w.id !== linked?.id);
  return (
    <Card
      title="Linked cash-out"
      actions={
        manage && choices.length > 0 ? (
          <Menu label={linked ? 'Change' : 'Link'} icon={Link2} compact>
            {(close) => (
              <>
                <div className="px-3 pb-1.5 pt-1 text-[12px] font-semibold text-stone">
                  This user’s recent cash-outs
                </div>
                {choices.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => {
                      close();
                      onLink(w);
                    }}
                    className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left transition hover:bg-canvas"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold">
                        {cashoutTitle(w)}
                      </span>
                      <span className="block text-[12px] text-stone">
                        <TimeAgo t={w.createdAt} />
                      </span>
                    </span>
                    <StatusPill status={w.status} />
                  </button>
                ))}
              </>
            )}
          </Menu>
        ) : undefined
      }
    >
      {linked ? (
        <>
          <Link
            to={`/withdrawals?tab=all&id=${linked.id}`}
            className="-mx-2 flex items-center gap-3 rounded-[10px] px-2 py-1.5 text-[13px] transition hover:bg-[#fbf8f3]"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{cashoutTitle(linked)}</span>
              <span className="block text-[12px] tabular-nums text-stone">
                {fmtNum(linked.coins)} coins · <TimeAgo t={linked.createdAt} />
              </span>
            </span>
            <StatusPill status={linked.status} />
          </Link>
          <p className="mt-2 text-[12px] leading-snug text-stone">
            {posted
              ? 'Support posted how it ended in this conversation.'
              : !OPEN_CASHOUT.has(linked.status)
                ? 'It had already ended when it was linked, so nothing is posted about it.'
                : closed
                  ? 'This ticket is closed, so nothing is posted here when it ends.'
                  : 'When it’s paid, rejected or fails, support posts that here. Paid closes the ticket unless someone still needs to look at it.'}
          </p>
          {manage && (
            <button
              type="button"
              onClick={() => onUnlink()}
              className="-mb-1 mt-1 inline-flex min-h-8 items-center text-[12px] font-semibold text-graphite underline underline-offset-2 hover:text-ink"
            >
              Unlink
            </button>
          )}
        </>
      ) : (
        <p className="text-[13px] leading-snug text-stone">
          {recent.length
            ? 'Not linked. Link the cash-out this ticket is about, and support posts here when it’s paid, rejected or fails.'
            : 'This user has no cash-outs.'}
        </p>
      )}
    </Card>
  );
}

/** Snippets added to the reply; the label is what the chip shows. */
const QUICK = [
  { label: 'Thanks', text: 'Thanks for reaching out! ' },
  {
    label: 'Credits take up to 24h',
    text: 'Offer credits can take up to 24 hours to arrive from the partner. ',
  },
  {
    label: 'Cash-out in review',
    text: 'Your cash-out is being reviewed and usually completes within a day. ',
  },
];

/**
 * A message as staff read it: English first. A user's message shows its translation with the
 * original a click away; a translated staff reply shows what staff wrote, and what the user read.
 */
/** Under a reply on an email ticket: sent (when), on its way, or not sent (why) with Send again. */
function EmailOutcome({
  m,
  ticketId,
  onDark,
  canReply,
}: {
  m: Msg;
  ticketId: string;
  onDark: boolean;
  canReply: boolean;
}) {
  const qc = useQueryClient();
  const cls = `mt-2 flex flex-wrap items-center gap-x-2 text-[12px] ${onDark ? 'text-white/65' : 'text-stone'}`;
  if (m.emailSentAt)
    return (
      <p className={cls}>
        <CircleCheck size={12} aria-hidden="true" /> Sent by email <TimeAgo t={m.emailSentAt} />
      </p>
    );
  if (!m.emailError) return <p className={cls}>Sending by email…</p>;
  return (
    <p className={`${cls} ${onDark ? 'text-amber-200' : 'text-berry-deep'}`}>
      Not sent by email: {m.emailError}.
      {canReply && (
        <button
          type="button"
          className="font-semibold underline underline-offset-2"
          onClick={() =>
            void run(
              () => api(`/support/${ticketId}/messages/${m.id}/resend`, { body: {} }),
              'Sending again. It shows as sent here once it has gone.',
            ).then((r) => {
              if (r) void qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
            })
          }
        >
          Send again
        </button>
      )}
    </p>
  );
}

function MessageBody({ m, onDark }: { m: Msg; onDark: boolean }) {
  const [original, setOriginal] = useState(false);
  if (!foreign(m.language) || !m.english)
    return <div className="whitespace-pre-wrap break-words">{m.body}</div>;
  const name = languageName(m.language);
  return (
    <>
      {original ? (
        <div lang={m.language} className="whitespace-pre-wrap break-words">
          {m.body}
        </div>
      ) : (
        <div className="whitespace-pre-wrap break-words">{m.english}</div>
      )}
      <button
        type="button"
        onClick={() => setOriginal((o) => !o)}
        aria-pressed={original}
        className={`-mb-1 mt-1 inline-flex min-h-8 items-center gap-1 text-[12px] font-semibold underline underline-offset-2 ${onDark ? 'text-white/70 hover:text-white' : 'text-stone hover:text-ink'}`}
      >
        <Languages size={12} aria-hidden="true" />
        {original
          ? 'Show in English'
          : m.author.startsWith('stf_')
            ? `Sent in ${name} · show what they read`
            : `Translated from ${name} · show original`}
      </button>
    </>
  );
}

/**
 * A user's screenshots and videos, inline. Only rebuilt copies exist (the originals were never
 * kept), served as images or video that can't run as a page; there is no download button.
 */
function TicketFiles({ files }: { files: NonNullable<Msg['attachments']> }) {
  const [open, setOpen] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const url = (id: string) => `/api/admin/support/attachments/${id}`;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {files.map((f) =>
        f.removed ? (
          <span
            key={f.id}
            className="rounded-[12px] border border-dashed border-hairline px-3 py-2 text-[12px] text-stone"
          >
            {f.kind === 'video' ? 'Video' : 'Image'} removed (kept 90 days after closing)
          </span>
        ) : f.kind === 'image' ? (
          <button
            key={f.id}
            type="button"
            onClick={() => {
              setZoomed(false);
              setOpen(f.id);
            }}
            className="overflow-hidden rounded-[12px] border border-hairline bg-canvas"
            title="Open full size"
          >
            <img
              src={url(f.id)}
              alt="Screenshot from the user"
              loading="lazy"
              width={f.width ?? undefined}
              height={f.height ?? undefined}
              className="block max-h-48 w-auto max-w-[220px] object-cover"
            />
          </button>
        ) : (
          <video
            key={f.id}
            src={url(f.id)}
            controls
            playsInline
            preload="metadata"
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            className="block max-h-72 w-[min(320px,100%)] rounded-[12px] bg-ink"
          />
        ),
      )}
      {open && (
        // Fitted to the window first (a phone screenshot is tall); a click shows it at actual size,
        // scrolling, for reading small text, and another click fits it again.
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Screenshot"
          onClick={() => setOpen(null)}
          className={`fixed inset-0 z-50 bg-ink/90 p-6 ${zoomed ? 'overflow-auto' : 'grid place-items-center'}`}
        >
          <img
            src={url(open)}
            alt="Screenshot from the user, full size"
            title={zoomed ? 'Click to fit the screen' : 'Click for actual size'}
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((z) => !z);
            }}
            onContextMenu={(e) => e.preventDefault()}
            className={
              zoomed
                ? 'mx-auto block h-auto w-auto max-w-none cursor-zoom-out rounded-[8px]'
                : 'block max-h-[calc(100dvh-3rem)] max-w-[calc(100vw-3rem)] cursor-zoom-in rounded-[8px] object-contain'
            }
          />
          <button
            type="button"
            autoFocus
            onClick={() => setOpen(null)}
            className="fixed right-4 top-4 rounded-full bg-white/15 px-4 py-2 text-[14px] font-semibold text-white hover:bg-white/25"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/support"
      className="-my-2 -ml-1 inline-flex min-h-10 items-center gap-1 rounded-[8px] px-1 text-[13px] font-semibold text-stone transition hover:text-ink"
    >
      <ArrowLeft size={14} /> Support
    </Link>
  );
}

export function SupportTicket() {
  const { id } = useParams();
  const qc = useQueryClient();
  const can = useCan();
  const canReply = can('support.reply');
  const canManage = can('support.manage');
  const { data, error, refetch } = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => api<TicketData>(`/support/${id}`),
    refetchInterval: 10_000,
  });
  useAssistantContext({ userId: data?.ticket.userId });
  const [body, setBody] = useState('');
  const manage = canManage;
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['support-ticket', id] }),
      qc.invalidateQueries({ queryKey: ['support'] }),
      qc.invalidateQueries({ queryKey: ['overview'] }),
    ]);

  // Messages not read in English yet are translated as the ticket opens (and as new ones arrive).
  const [translating, setTranslating] = useState<'idle' | 'busy' | 'failed'>('idle');
  const asked = useRef(new Set<string>());
  const untranslated =
    data?.canTranslate &&
    data.messages.filter((m) => m.language === null && !m.author.startsWith('stf_'));
  const pendingIds = untranslated ? untranslated.map((m) => m.id).join(',') : '';
  async function translate() {
    setTranslating('busy');
    try {
      await api(`/support/${id}/translate`, { body: {} });
      setTranslating('idle');
      await qc.invalidateQueries({ queryKey: ['support-ticket', id] });
    } catch {
      setTranslating('failed');
    }
  }
  useEffect(() => {
    if (!pendingIds || asked.current.has(pendingIds)) return;
    asked.current.add(pendingIds);
    void translate();
  }, [pendingIds]);

  // Replies go out in the user's language unless staff choose English.
  const userLanguage =
    data?.canTranslate && foreign(data.ticket.language) ? data.ticket.language : null;
  const [inEnglish, setInEnglish] = useState(false);
  const [preview, setPreview] = useState<{
    for: string;
    language: string;
    translation: string;
    backToEnglish: string;
  } | null>(null);
  const shownPreview = preview && preview.for === body ? preview : null;

  async function send(close: boolean, translation?: { language: string; text: string }) {
    const ok = await run(
      () =>
        api(`/support/${id}/reply`, {
          body: { body, close, ...(translation ? { translation } : {}) },
        }),
      close
        ? 'Sent and closed.'
        : data?.ticket.status === 'on_hold' && data.ticket.snoozedUntil
          ? `Reply sent. Still on hold until ${wakeTime(data.ticket.snoozedUntil)}.`
          : 'Reply sent. The user gets an email.',
    );
    if (ok !== undefined) {
      setBody('');
      setPreview(null);
    }
    await refresh();
  }
  const needsPreview = !!userLanguage && !inEnglish && !shownPreview;
  const [previewing, setPreviewing] = useState(false);
  /** Translate the reply and show it; sending then sends exactly what staff saw. */
  async function previewReply() {
    setPreviewing(true);
    const r = await run(() =>
      api<{ language: string; translation: string; backToEnglish: string }>(
        `/support/${id}/translate-reply`,
        { body: { body, language: userLanguage } },
      ),
    );
    setPreviewing(false);
    if (r) setPreview({ for: body, ...r });
  }
  function reply(close: boolean) {
    if (needsPreview) return previewReply();
    return send(
      close,
      userLanguage && !inEnglish && shownPreview
        ? { language: shownPreview.language, text: shownPreview.translation }
        : undefined,
    );
  }
  async function setStatus(status: string) {
    await run(
      () => api(`/support/${id}/status`, { body: { status } }),
      status === 'closed' ? 'Ticket closed.' : 'Ticket reopened.',
    );
    await refresh();
  }
  async function hold(choice: HoldChoice, note: string) {
    const r = await run(() =>
      api<{ snoozedUntil: number }>(`/support/${id}/hold`, {
        body: { ...choice, ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    );
    if (r) toast(`On hold until ${wakeTime(r.snoozedUntil)}.`);
    await refresh();
  }
  async function wake() {
    await run(() => api(`/support/${id}/wake`, { body: {} }), 'Back in Needs staff.');
    await refresh();
  }
  async function link(withdrawalId: string | null) {
    const r = await run(() =>
      api<{ follows: boolean }>(`/support/${id}/cashout`, { body: { withdrawalId } }),
    );
    // Says what will happen: a closed ticket, or a cash-out that already ended, hears nothing.
    if (r)
      toast(
        !withdrawalId
          ? 'Cash-out unlinked.'
          : r.follows
            ? 'Linked. Support will post here when the cash-out ends.'
            : 'Linked. Nothing will be posted about it here.',
      );
    await refresh();
  }

  if (error && !data)
    return (
      <div className="space-y-5">
        <BackLink />
        <ErrorNote error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!data)
    return (
      <div className="space-y-5" aria-busy="true">
        <BackLink />
        <div className="space-y-2">
          <div className="h-5 w-40 animate-pulse rounded-full bg-ink/[0.07]" />
          <div className="h-9 w-2/3 animate-pulse rounded-[10px] bg-ink/[0.07]" />
        </div>
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-3">
            <div className="h-20 w-3/4 animate-pulse rounded-[18px] bg-ink/[0.06]" />
            <div className="ml-auto h-24 w-3/4 animate-pulse rounded-[18px] bg-ink/[0.06]" />
            <div className="card h-56 animate-pulse" />
          </div>
          <div className="card h-64 animate-pulse" />
        </div>
      </div>
    );
  const t = data.ticket;
  const closed = t.status === 'closed';
  const held = t.status === 'on_hold';
  const holder = t.snoozedBy ? (data.staffNames[t.snoozedBy] ?? 'Staff') : 'Staff';
  return (
    <div className="space-y-5">
      <BackLink />
      <PageHeader
        title={t.subject ?? 'Ticket'}
        eyebrow={
          <span className="inline-flex flex-wrap items-center gap-2">
            <StatusPill status={t.status} />
            {t.channel === 'email' && (
              <Pill tone="sky">
                <Mail size={11} aria-hidden="true" /> Email
              </Pill>
            )}
            <span>
              Opened <TimeAgo t={t.createdAt} />
            </span>
          </span>
        }
        actions={
          <>
            {/* Claude drafts FAQ answers from this conversation, for review (pages/Faq.tsx). */}
            {can('faq.manage') && data.messages.length > 0 && (
              <Link
                to={`/faq/from-ticket/${t.id}`}
                className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]"
              >
                <MessageCircleQuestion size={15} /> Turn into FAQ
              </Link>
            )}
            {canManage && !closed && !held && <HoldMenu onHold={(c, n) => void hold(c, n)} />}
            {canManage && held && (
              <button
                className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]"
                onClick={() => void wake()}
              >
                <AlarmClock size={15} /> Wake now
              </button>
            )}
            {!canManage ? null : !closed ? (
              <button
                className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]"
                onClick={() => setStatus('closed')}
              >
                <CircleCheck size={15} /> Close ticket
              </button>
            ) : (
              <button
                className="btn-ghost min-h-10 px-3.5 py-2 text-[14px]"
                onClick={() => setStatus('pending_staff')}
              >
                <RotateCcw size={15} /> Reopen
              </button>
            )}
          </>
        }
      />
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          {held && t.snoozedUntil ? (
            <section
              aria-label="On hold"
              className="flex items-start gap-3 rounded-[14px] border border-hairline bg-paper px-4 py-3.5"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink/[0.06] text-ink"
              >
                <AlarmClock size={16} />
              </span>
              <div className="min-w-0 flex-1 text-[14px] leading-relaxed">
                <p className="font-bold">
                  On hold until {wakeTime(t.snoozedUntil)}{' '}
                  <span className="font-normal text-stone">
                    (<TimeAgo t={t.snoozedUntil} />)
                  </span>
                </p>
                <p className="text-[13px] text-graphite">
                  Back in Needs staff then, or as soon as the user writes. They see “In progress”.
                </p>
                {t.snoozeNote && (
                  <blockquote className="mt-2 border-l-2 border-ink/15 pl-3 text-[13px] text-graphite">
                    <p className="whitespace-pre-wrap break-words">{t.snoozeNote}</p>
                    <footer className="mt-0.5 text-[12px] text-stone">{holder}</footer>
                  </blockquote>
                )}
              </div>
            </section>
          ) : (
            t.snoozeNote &&
            !closed && (
              <p className="flex items-start gap-2 text-[13px] text-stone">
                <AlarmClock size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span className="min-w-0 whitespace-pre-wrap break-words">
                  {holder}’s note from when it was on hold: {t.snoozeNote}
                </span>
              </p>
            )
          )}
          {t.aiNote && (
            <section
              aria-label="Claude’s note for the team"
              className="flex items-start gap-3 rounded-[14px] border border-sky-500/15 bg-sky-500/[0.06] px-4 py-3.5"
            >
              <span
                aria-hidden="true"
                className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-500/10 text-ink"
              >
                <Bot size={16} />
              </span>
              <div className="min-w-0 flex-1 text-[14px] leading-relaxed">
                <p className="font-bold">
                  Claude’s note for the team{' '}
                  <span className="font-normal text-stone">· the user doesn’t see this</span>
                </p>
                <p className="mt-1 whitespace-pre-wrap break-words text-[13px] text-graphite">
                  {t.aiNote}
                </p>
              </div>
            </section>
          )}
          {translating !== 'idle' && (
            <p
              role="status"
              className="flex items-center gap-2 text-[13px] font-semibold text-stone"
            >
              <Languages size={14} aria-hidden="true" />
              {translating === 'busy' ? (
                'Translating to English…'
              ) : (
                <>
                  Couldn’t translate this ticket.
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-ink"
                    onClick={() => void translate()}
                  >
                    Try again
                  </button>
                </>
              )}
            </p>
          )}
          {data.messages.length === 0 ? (
            <p className="rounded-[14px] border border-dashed border-ink/20 px-4 py-6 text-center text-[14px] text-stone">
              No messages on this ticket yet.
            </p>
          ) : (
            <ol className="space-y-3" aria-label="Conversation">
              {data.messages.map((m) => {
                const auto = SYSTEM_MESSAGES[m.author];
                if (auto)
                  return (
                    <li key={m.id} className="flex justify-center">
                      <div className="w-full max-w-[88%] rounded-[14px] border border-dashed border-ink/25 bg-canvas px-4 py-3 text-[14px] leading-relaxed text-graphite sm:max-w-[80%]">
                        <div className="mb-1 flex flex-wrap items-center gap-x-1.5 text-[12px] font-semibold text-stone">
                          <auto.icon size={13} />
                          <span className="text-graphite">{auto.label}</span>
                          <span aria-hidden="true">·</span>
                          <TimeAgo t={m.createdAt} />
                        </div>
                        <MessageBody m={m} onDark={false} />
                      </div>
                    </li>
                  );
                const fromUser = m.author.startsWith('usr_') || m.author.startsWith('email:');
                const ai = m.author.startsWith('ai:');
                const escalated = m.author === 'ai:support:escalated';
                const staff = !fromUser && !ai;
                return (
                  <li key={m.id} className={`flex ${fromUser ? 'justify-start' : 'justify-end'}`}>
                    <div
                      className={`max-w-[88%] rounded-[18px] px-4 py-3 text-[14px] leading-relaxed sm:max-w-[80%] ${
                        fromUser
                          ? 'rounded-tl-[6px] border border-hairline bg-paper'
                          : escalated
                            ? 'rounded-tr-[6px] border border-coin/40 bg-coin-tint/60'
                            : ai
                              ? 'rounded-tr-[6px] border border-sky-500/15 bg-sky-500/10'
                              : 'rounded-tr-[6px] bg-ink text-white'
                      }`}
                    >
                      <div
                        className={`mb-1 flex flex-wrap items-center gap-x-1.5 text-[12px] font-semibold ${staff ? 'text-white/65' : 'text-stone'}`}
                      >
                        {ai && <Bot size={13} aria-hidden="true" />}
                        <span className={staff ? 'text-white' : 'text-graphite'}>
                          {fromUser
                            ? (t.username ?? 'User')
                            : escalated
                              ? 'Claude · handed to staff'
                              : actorLabel(m.author, data.staffNames)}
                        </span>
                        <span aria-hidden="true">·</span>
                        <TimeAgo t={m.createdAt} />
                      </div>
                      {m.body && <MessageBody m={m} onDark={staff} />}
                      {m.email && (m.email.droppedFiles.length > 0 || !m.email.authenticated) && (
                        <p className="mt-2 text-[12px] text-stone">
                          {!m.email.authenticated && 'Sender not verified by Google. '}
                          {m.email.droppedFiles.length > 0 &&
                            `Files not kept: ${m.email.droppedFiles.join(', ')}`}
                        </p>
                      )}
                      {t.channel === 'email' && !fromUser && (
                        <EmailOutcome m={m} ticketId={t.id} onDark={staff} canReply={canReply} />
                      )}
                      {m.attachments && m.attachments.length > 0 && (
                        <TicketFiles files={m.attachments} />
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {!canReply ? (
            <ViewOnly>Replying to users isn’t part of your roles.</ViewOnly>
          ) : (
            closed &&
            !canManage && (
              <ViewOnly>
                This conversation is closed, and reopening it isn’t part of your roles.
              </ViewOnly>
            )
          )}
          {t.channel === 'email' && canReply && (
            <p className="flex items-start gap-2 text-[13px] text-stone">
              <Mail size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                This conversation is by email: your reply is sent to{' '}
                <span className="font-semibold text-ink">{data.user?.email ?? t.email}</span> from
                support@gaintplay.com, with the usual translation.
              </span>
            </p>
          )}
          <div className="card p-4 sm:p-5" hidden={!canReply || (closed && !canManage)}>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-[12px] font-semibold text-stone">Quick replies</span>
              {QUICK.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  title={q.text.trim()}
                  onClick={() => setBody((b) => b + q.text)}
                  className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-paper px-3 text-[12px] max-sm:h-10 font-semibold text-graphite transition hover:border-ink hover:text-ink"
                >
                  <Plus size={12} aria-hidden="true" />
                  {q.label}
                </button>
              ))}
            </div>
            <label htmlFor="reply" className="sr-only">
              Reply
            </label>
            <textarea
              id="reply"
              className="input mt-3 min-h-32 text-[14px] leading-relaxed"
              placeholder={`Reply to ${t.username ?? 'the user'} as GaintPlay support${userLanguage && !inEnglish ? `, in English: it goes out in ${languageName(userLanguage)}` : ''}`}
              value={body}
              maxLength={4000}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && body.trim()) void reply(false);
              }}
            />
            {userLanguage && (
              <label className="mt-3 flex items-center gap-2 text-[13px] text-graphite">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-ink"
                  checked={inEnglish}
                  onChange={(e) => setInEnglish(e.target.checked)}
                />
                Send in English instead of {languageName(userLanguage)}
              </label>
            )}
            {userLanguage && !inEnglish && shownPreview && (
              <div className="mt-3 space-y-3 rounded-[12px] border border-hairline bg-canvas p-3 text-[14px] leading-relaxed">
                <div>
                  <div className="text-[12px] font-semibold text-stone">
                    They’ll read, in {languageName(shownPreview.language)}
                  </div>
                  <p lang={shownPreview.language} className="mt-1 whitespace-pre-wrap break-words">
                    {shownPreview.translation}
                  </p>
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-stone">
                    Back in English, to check it says what you meant
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-graphite">
                    {shownPreview.backToEnglish}
                  </p>
                </div>
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {needsPreview ? (
                <button
                  className="btn-primary"
                  disabled={!body.trim() || previewing}
                  onClick={() => void previewReply()}
                >
                  <Languages size={15} />{' '}
                  {previewing ? 'Translating…' : `Translate to ${languageName(userLanguage!)}`}
                </button>
              ) : (
                <>
                  <button
                    className="btn-primary"
                    disabled={!body.trim()}
                    onClick={() => void reply(false)}
                  >
                    <Send size={15} /> Send
                    {userLanguage && !inEnglish ? ` in ${languageName(userLanguage)}` : ''}
                  </button>
                  {canManage && (
                    <button
                      className="btn-ghost"
                      disabled={!body.trim()}
                      onClick={() => void reply(true)}
                    >
                      Send and close
                    </button>
                  )}
                </>
              )}
              <span className="w-full text-[12px] text-stone sm:ml-auto sm:w-auto sm:text-right">
                <kbd className="font-sans font-semibold text-graphite">Ctrl/⌘ + Enter</kbd>{' '}
                {needsPreview ? 'translates first.' : 'sends.'} The user gets your reply by email
                {held ? '; the ticket stays on hold.' : '.'}
              </span>
            </div>
          </div>
        </div>

        <aside className="space-y-4" aria-label="About this user">
          {data.user ? (
            <Card title="Account">
              <UserChip
                id={data.user.id}
                username={data.user.username}
                sub={data.user.email}
                deleted={data.user.status === 'deleted'}
              />
              <div className="mt-3 flex flex-wrap gap-1.5">
                <StatusPill status={data.user.status} />
                {!data.user.emailVerifiedAt && data.user.status !== 'deleted' && (
                  <Pill tone="sun">Email not verified</Pill>
                )}
              </div>
              {data.balance && (
                <div className="mt-4">
                  <div className="mb-1 text-[12px] font-semibold text-stone">Balance in coins</div>
                  <dl className="divide-y divide-hairline rounded-[12px] bg-canvas px-3 text-[13px]">
                    {(
                      [
                        ['Available', data.balance.available, true],
                        ['Pending', data.balance.pending, false],
                        ['Cashing out', data.balance.withdrawing, false],
                      ] as const
                    ).map(([k, v, money]) => (
                      <div key={k} className="flex items-baseline justify-between gap-3 py-2">
                        <dt className="text-graphite">{k}</dt>
                        <dd
                          className={`font-extrabold tabular-nums ${money ? 'text-coin-ink' : ''}`}
                        >
                          {fmtNum(v)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              <p className="mt-3 text-[12px] text-stone">
                Joined <TimeAgo t={data.user.createdAt} />
              </p>
            </Card>
          ) : (
            <Card title="Account">
              <p className="text-[14px] text-stone">
                {t.channel === 'email' ? (
                  <>
                    Wrote by email from{' '}
                    <span className="break-all font-semibold text-ink">{t.email}</span>, an address
                    on no account, so there is no balance or history to show. Claude treats them as
                    a stranger: no account facts, no confirmation that any account exists.
                  </>
                ) : (
                  'Sent without an account, so there is no balance or history to show.'
                )}
              </p>
            </Card>
          )}
          {data.user && (
            <CashoutCard
              linked={data.linkedWithdrawal}
              recent={data.recentWithdrawals}
              manage={manage}
              closed={closed}
              posted={t.cashoutOutcome}
              onLink={(w) => void link(w.id)}
              onUnlink={() => void link(null)}
            />
          )}
          {data.recentWithdrawals.length > 0 && (
            <Card title="Recent cash-outs" pad={false}>
              <ul className="divide-y divide-hairline">
                {data.recentWithdrawals.map((w) => (
                  <li key={w.id}>
                    <Link
                      to={`/withdrawals?tab=all&id=${w.id}`}
                      className="flex items-center gap-3 px-5 py-3 text-[13px] transition hover:bg-[#fbf8f3] focus-visible:-outline-offset-2"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold">
                          {w.item
                            ? `${fmtMoney(w.item.amount, w.item.productCurrency ?? 'USD')} ${w.item.productName}`
                            : `${fmtNum(w.coins)} coins`}
                        </span>
                        <span className="block text-[12px] text-stone">
                          <TimeAgo t={w.createdAt} />
                        </span>
                      </span>
                      <StatusPill status={w.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
